/**
 * Fullscreen with the Keyboard Lock API (Chromium only).
 *
 * Holding Ctrl while also pressing WASD is only safe when the browser isn't
 * listening for its own shortcuts — Ctrl+W closes the tab, and no page can
 * preventDefault it. Keyboard Lock in fullscreen hands those keys to the page,
 * so the Shift/Ctrl throttle is gated on `isKeyboardLocked()` and simply does
 * nothing anywhere else (the scroll wheel is always throttle).
 *
 * Fullscreen has to start from a user gesture, so this is driven by a button
 * (Settings → Controls) rather than a persisted setting. While locked, Chrome
 * leaves fullscreen on a press-and-hold of Esc; a tap still reaches the game.
 */

let _locked = false;
const _listeners = new Set();

function notify() { _listeners.forEach((fn) => fn(_locked)); }

if (typeof document !== 'undefined') {
  document.addEventListener('fullscreenchange', () => {
    if (!document.fullscreenElement) {
      _locked = false;
      try { navigator.keyboard?.unlock(); } catch { /* not supported */ }
    }
    notify();
  });
}

/** True while fullscreen *and* the keyboard is locked to the page. */
export function isKeyboardLocked() {
  return _locked && !!document.fullscreenElement;
}

export function isFullscreen() {
  return !!document.fullscreenElement;
}

/** Whether this browser can lock the keyboard at all (Chromium). */
export function keyboardLockSupported() {
  return typeof navigator !== 'undefined' && !!navigator.keyboard?.lock;
}

/** Enter fullscreen and lock the keyboard, or leave fullscreen. Call from a click. */
export async function toggleFullscreen() {
  if (document.fullscreenElement) {
    await document.exitFullscreen().catch(() => {});
    return;
  }
  try {
    await document.documentElement.requestFullscreen({ navigationUI: 'hide' });
  } catch {
    return;
  }
  if (keyboardLockSupported()) {
    try {
      await navigator.keyboard.lock();
      _locked = true;
    } catch {
      _locked = false;
    }
  }
  notify();
}

/** Subscribe to fullscreen/lock changes. Returns an unsubscribe function. */
export function onKeyboardLockChange(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}
